if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.initOrderLimitLock();
      }

      disconnectedCallback() {
        this.orderLimitObserver?.disconnect();
      }

      // Watch for an order-limit / MOQ module's warning message (Melon renders
      // `.wm-limit-box`) and disable the add-to-cart button while it is shown.
      // The lock is applied without the theme's own `data-theme-disabled` tag,
      // so `toggleSubmitButton` treats it as external and won't strip it on a
      // variant change; `onSubmitHandler` then blocks the submit.
      initOrderLimitLock() {
        const scope = this.closest('product-info') || this.closest('.product__info-container') || this;
        const MESSAGE_SELECTOR = '.wm-limit-box';

        const isVisible = (el) => !!el && el.getClientRects().length > 0;
        const evaluate = () => this.toggleOrderLimitLock(isVisible(scope.querySelector(MESSAGE_SELECTOR)));

        evaluate();
        this.orderLimitObserver = new MutationObserver(evaluate);
        // Watch child add/remove and style/class changes (the module toggles
        // the message via `display`), but NOT the button's own attribute writes.
        this.orderLimitObserver.observe(scope, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden'],
        });
      }

      toggleOrderLimitLock(locked) {
        const btn = this.submitButton;
        if (!btn) return;
        if (locked) {
          if (btn.dataset.moqLocked === 'true') return;
          btn.dataset.moqLocked = 'true';
          btn.setAttribute('disabled', 'disabled');
        } else {
          if (btn.dataset.moqLocked !== 'true') return;
          delete btn.dataset.moqLocked;
          // Only lift the lock if the theme isn't also holding the button
          // disabled (e.g. sold out / unavailable).
          if (btn.dataset.themeDisabled !== 'true') btn.removeAttribute('disabled');
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        // Block submit if the button is disabled by any means, including a
        // native `disabled` applied by an external MOQ / order-limit module.
        if (
          this.submitButton.getAttribute('aria-disabled') === 'true' ||
          this.submitButton.hasAttribute('disabled')
        )
          return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
    
            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          // Tag disables the theme itself owns (sold out / unavailable) so we
          // can tell them apart from a lock set by an external module.
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.dataset.themeDisabled = 'true';
          if (text) this.submitButtonText.textContent = text;
        } else {
          const themeOwned = this.submitButton.dataset.themeDisabled === 'true';
          // An external MOQ / order-limit module has locked the button if it
          // carries a disabled state the theme did not set itself.
          const externallyLocked =
            this.submitButton.getAttribute('aria-disabled') === 'true' ||
            (this.submitButton.hasAttribute('disabled') && !themeOwned);

          delete this.submitButton.dataset.themeDisabled;

          if (!externallyLocked) {
            this.submitButton.removeAttribute('disabled');
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
